const project_list_payload = `
	id
	databaseId
	name
	state
	createdAt
	updatedAt
	revisionName
	scopeCreateVersion
	revisionGroupName
	revisionNumber
	revisionNotes
	createdBy {
		id
		databaseId
		name
	}
	updatedBy {
		id
		databaseId
		name
	}
	permissions {
		canUpdate {
			value
		}
		canDelete {
			value
		}
		canReview {
			value
		}
		canManage {
			value
		}
	}
	resourcePermissions {
		id
		permission
		permissableType
		permissable {
			... on User {
				id
				databaseId
				name
				licenses {
					nodes {
						type
					}
				}
			}
			... on Group {
				id
				databaseId
				name
				members(
					first: 10, licenseTypes: [AUTHOR, VIEWER, REVIEWER, ADMIN], orderBy: { attribute: "name", direction: ASC }
				) {
					totalCount
					nodes {
						id
						databaseId
						name
						licenses {
							nodes {
								type
							}
						}
					}
				}
			}
		}
	}
`

export const LIST_PROJECTS = `
	query ListProjects(
		$first: Int,
		$afterIndex: Int,
		$orderBy: SortOrderInputObject,
		$name: String,
		$createdByIds: [ID!],
		$createdByDatabaseIds: [String!],
		$updatedByIds: [ID!],
		$updatedAt: DateRangeInputObject,
		$sharedWithIds: [ID!],
		$sharedWithDatabaseIds: [String!],
		$scopeCreateVersions: [String!],
		$states: [ProjectStateEnum!],
		$ids: [ID!],
		$after: String,
		$before: String,
		$last: Int,
		$beforeIndex: Int
	) {
  projects(
    first: $first
    afterIndex: $afterIndex
    orderBy: $orderBy
    name: $name
    createdByIds: $createdByIds
    createdByDatabaseIds: $createdByDatabaseIds
    updatedByIds: $updatedByIds
    updatedAt: $updatedAt
    sharedWithIds: $sharedWithIds
    sharedWithDatabaseIds: $sharedWithDatabaseIds
    scopeCreateVersions: $scopeCreateVersions
    states: $states
    ids: $ids
    after: $after
    before: $before
    last: $last
    beforeIndex: $beforeIndex
    sharedList: true
		latestRevisionOnly: true
  ) {
			nodes {
				${project_list_payload}
				revisions {
					${project_list_payload}
				}
			}
			totalCount
	    creators {
	      id
	      databaseId
	      name
	    }
	    permissables {
	      ...on User {
	        id
	        databaseId
	        name
	      }
	      ...on Group {
	        id
	        databaseId
	        name
	      }
	    }
		}
  }
`;

export const GET_PROJECT_BY_ID = `
	query GetProjectById($id: ID, $databaseId: String, $opened: Boolean) {
		project(id: $id, databaseId: $databaseId, opened: $opened) {
			id
			databaseId
			name
			state
			createdAt
			revisionName
			scopeCreateVersion
			updatedAt
			revisionName
			revisionGroupName
			revisionNumber
			version
			createdBy {
				id
				databaseId
				name
				createdAt
			}
			updatedBy {
				id
				databaseId
				name
				updatedAt
			}
			auditLogs(data: { path: "changes.state" }, orderBy: { attribute: "date", direction: DESC }) {
				nodes {
					data
				  date
				  userName
				  user {
						databaseId
				  }
				}
			}
			permissions {
				canView {
					value
				}
				canUpdate {
					value
				}
				canDelete {
					value
				}
				canReview {
					value
				}
				canManage {
					value
				}
			}
			resourcePermissions {
				id
				permission
				permissableType
				permissable {
					... on User {
						id
						databaseId
						name
						licenses {
							nodes {
								type
							}
						}
					}
					... on Group {
						id
						databaseId
						name
						members(
							first: 10, licenseTypes: [AUTHOR, VIEWER, REVIEWER, ADMIN], orderBy: { attribute: "name", direction: ASC }
						) {
							totalCount
							nodes {
								id
								databaseId
								name
								licenses {
									nodes {
										type
									}
								}
							}
						}
					}
				}
			}
			scenarios {
				id
				databaseId
				name
				currentRelease {
					sdkVersion
					type
					author {
						id
						databaseId
						name
						email
						username
						permalink
					}
					publishedAt
				}
				originGroup {
					id
					name
					scenarioRecipientGroups {
						nodes {
							id
							name
						}
					}
				}
				recipientGroups {
					nodes {
						id
						name
					}
				}
				resourcePermissions {
					id
					permission
					permissableType
					permissable {
						... on User {
							id
							databaseId
							name
							licenses {
								nodes {
									type
								}
							}
						}
						... on Group {
							id
							databaseId
							name
							members(
								first: 10, licenseTypes: [AUTHOR, VIEWER, REVIEWER, ADMIN], orderBy: { attribute: "name", direction: ASC }
							) {
								totalCount
								nodes {
									id
									databaseId
									name
									licenses {
										nodes {
											type
										}
									}
								}
							}
						}
					}
				}
			}
		}
	}
`;

export const CREATE_PROJECT = `
	mutation CreateProject($input: CreateProjectMutationInput!) {
		createProject(input: $input) {
			project {
				id
				databaseId
				name
				revisionGroupName
				revisionName
				revisionNumber
				createdAt
				scopeCreateVersion
				version
				createdBy {
					id
					databaseId
					name
					createdAt
				}
			}
			errors {
				message
				fullMessage
				path
				type
			}
		}
	}
`;

export const UPDATE_PROJECT = `
	mutation UpdateProject($input: UpdateProjectMutationInput!) {
		updateProject(input: $input) {
			project {
				id
				databaseId
				name
				state
				resourcePermissions {
					id
					permission
					permissableType
					permissable {
						... on User {
							id
							databaseId
							name
							initials
							licenses {
								nodes {
									type
								}
							}
						}
						... on Group {
							id
							databaseId
							name
							members {
								totalCount
							}
						}
					}
				}
			}
			errors {
				message
				fullMessage
				path
				type
			}
		}
	}
`;

export const DELETE_PROJECT = `
	mutation DeleteProject($input: DeleteProjectMutationInput!) {
		deleteProject(input: $input) {
			success
			errors {
				message
				fullMessage
				path
				type
			}
		}
	}
`;

export const DELETE_PROJECTS = `
	mutation DeleteProjects($input: DeleteProjectsMutationInput!) {
		deleteProjects(input: $input) {
			success
			errors {
				message
				fullMessage
				path
				type
			}
		}
	}
`;

export const SAVE_PROJECT = `
	mutation SaveProject($input: CreateProjectSaveFileMutationInput!) {
		createProjectSaveFile(input: $input) {
			projectSaveFile {
				id
				databaseId
				project {
					id
					databaseId
					name
				}
			}
			errors {
				message
				fullMessage
				path
				type
			}
		}
	}
`;

export const GET_PROJECT_SAVE_FILE_ID = `
query GetProjectSaveFileId($databaseId: String) {
		project(databaseId: $databaseId) {
			id
      currentSaveFile {
        id
      }
		}
	}
`;

export const LOAD_PROJECT_SAVE_FILES = `
	query LoadProjectSaveFiles($databaseId: String, $lastSaveFiles: Int) {
	  project(databaseId: $databaseId) {
	    id
	    name
	    manualSavefiles: saveFiles(last: $lastSaveFiles, saveTypes: [MANUAL], orderBy: { attribute: "createdAt", direction: ASC }) {
	      nodes {
	        id
	        createdAt
	        createdBy {
	          id
	          databaseId
	          username
	          email
	        }
	        scopeCreateVersion
	        extensionsVersion
	        file {
	          fileUrl
	        } 
	      }
	    }
	    autoSavefiles: saveFiles(last: $lastSaveFiles, saveTypes: [AUTO], orderBy: { attribute: "createdAt", direction: ASC }) {
	      nodes {
	        id
	        createdAt
	        createdBy {
	          id
	          databaseId
	          username
	          email
	        }
	        scopeCreateVersion
	        extensionsVersion
	        file {
	          fileUrl
	        } 
	      }
	    }
	  }
	}
`;

export const GET_PROJECT_FOR_EXPORT = `
	query GetProjectForExport($databaseId: String) {
	  project(databaseId: $databaseId) {
	    id
	    name
	    queuedOrProcessingJobs: projectExportJobs(
	    	orderBy: { attribute: "queuedAt", direction: DESC }, processStatus: [QUEUED, PROCESSING]
	    ) {
	    	nodes {
		    	id
		    	databaseId
		    	processStatus
	    	}
	    }
	    manualSavefiles: saveFiles(first: 1, saveTypes: [MANUAL], orderBy: { attribute: "createdAt", direction: DESC }) {
	      nodes {
	        id
	        databaseId
	      }
	    }
	  }
	}
`;

export const IMPORT_PROJECT = `
	mutation ImportProject($input: CreateProjectImportJobMutationInput!) {
		createProjectImportJob(input: $input) {
			projectImportJob {
				id
				databaseId
				processStatus
				project {
					id
					databaseId
				}
			}
			errors {
				message
				fullMessage
				path
				type
			}
		}
	}
`;

export const GET_PROJECT_JOBS_LIST = `
	query GetProjectPendingJobs($databaseId: String) {
	  project(databaseId: $databaseId) {
	    id
	    name
	    projectExportJobs(orderBy: { attribute: "queuedAt", direction: ASC }) {
	    	nodes {
		    	id
		    	databaseId
					assetPackaging
		    	processStatus
					queuedAt
					processStartedAt
					processEndedAt
					projectSaveFile {
		        id
		        databaseId
		        createdAt
		        scopeCreateVersion
		        extensionsVersion
					}
					file {
						fileUrl
						fileSize
					}
	    	}
	    }
	    projectImportJobs(orderBy: { attribute: "queuedAt", direction: ASC }) {
	    	nodes {
		    	id
		    	databaseId
		    	processStatus
					queuedAt
					processStartedAt
					processEndedAt
					modelProcessJobs {
						nodes {
							id
							databaseId
							processStatus
							revisionName
							asset {
								id
								databaseId
								name
								type
							}
						}
					}
	    	}
	    }
	  }
	}
`;

export const GET_PROJECT_MERGE_JOBS_LIST = `
	query GetProjectPendingMergeJobs($projectDatabaseId: String) {
	  projectMergeJobs(orderBy: { attribute: "queuedAt", direction: ASC }, projectDatabaseId: $projectDatabaseId) {
    	nodes {
	    	id
	    	databaseId
	    	processStatus
				file {
					fileUrl
				}
				modifiedFile {
					fileUrl
				}
    	}
	  }
	}
`;

export const GET_DEMO_PROJECTS_LIST = `
	query GetDemoProjectsList {
		demoProjects {
			nodes {
				name
				previewFile {
					fileUrl
				}
				jsonFile {
					fileUrl
				}
			}
		}
	}
`;

export const GET_PROJECT_FOR_EXPORT_REVISION = `
	query GetProjectForExportRevision($databaseId: String, $projectSaveFileDatabaseId: String!) {
	  project(databaseId: $databaseId) {
	    id
	    name
	    queuedOrProcessingJobs: projectExportJobs(
	    	orderBy: { attribute: "queuedAt", direction: DESC }, processStatus: [QUEUED, PROCESSING]
	    ) {
	    	nodes {
		    	id
		    	databaseId
		    	processStatus
	    	}
	    }
	    saveFiles(first: 1, databaseIds: [$projectSaveFileDatabaseId]) {
	      nodes {
	        id
	        databaseId
	      }
	    }
	  }
	}
`;

export const GET_PROJECT_EXPORT_JOB = `
	query GetProjectExportJob($projectDatabaseId: String, $databaseId: String!) {
	  projectExportJobs(projectDatabaseId: $projectDatabaseId, databaseIds: [$databaseId]) {
	  	nodes {
	  		id
	  		databaseId
	  		processStatus
	  		file {
	  			fileUrl
	  		}
	  	}
	  }
	}
`;

export const EXPORT_PROJECT = `
	mutation ExportProject($input: CreateProjectExportJobMutationInput!) {
		createProjectExportJob(input: $input) {
			projectExportJob {
				id
				databaseId
				assetPackaging
				processStatus
				project {
					id
					databaseId
				}
			}
			errors {
				message
				fullMessage
				path
				type
			}
		}
	}
`;

export const PUBLISH_VERSION = `
	mutation PublishVersion($input: UpdateScenarioMutationInput!) {
		updateScenario(input: $input) {
			scenario {
				id
				databaseId
			}
			errors {
				message
				fullMessage
				path
				type
			}
		}
	}
`;
export const LIST_PROJECT_REVISIONS = `
query ListProjectRevisions($projectId: String){
  project(databaseId: $projectId){
    id
    revisionGroupName
    revisionName
    revisionNotes
    revisionNumber
    revisions {
		      currentSaveFile {
        project {
          id
        }
      }
      revisionName
      revisionNotes
      revisionNumber
    }
  }
}
`;

export const UPDATE_PROJECT_REVISION = `
	mutation UpdateProjectRevisionDetails($input: UpdateProjectMutationInput!){
  updateProject(input: $input){
    project {
      id
      revisionGroupName
      revisionName
      revisionNumber
      revisionNotes
			revisions {
      currentSaveFile {
        project {
          id
        }
      }
      	revisionName
      	revisionNotes
      	revisionNumber
    }
    }
  }
}
`;

export const CREATE_PROJECT_REVISION = `
	mutation CreateProjectRevision($input: CreateProjectMutationInput!) {
  createProject(input: $input) {
    project {
      id
      databaseId
      name
      revisionGroupName
      revisionName
      revisionNumber
			revisions {
      currentSaveFile {
        project {
          id
        }
      }
      	revisionName
      	revisionNotes
      	revisionNumber
    }
    }
    errors {
      message
      fullMessage
      path
      type
    }
  }
}
`;